import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";

/**
 * Shared shell for marketing pages (/vendors, /members, /clubs, /pricing,
 * /about, /contact) per Prompt 2. Provides the new top nav (Home · Solutions ▾
 * · Pricing · About · Contact · Login) + the multi-audience footer.
 * Uses the existing luxury aesthetic (Cormorant serif + deep green + cream).
 */
export default function MarketingLayout({ children, activePath }) {
  const { pathname } = useLocation();
  const path = activePath || pathname;
  return (
    <div className="min-h-screen flex flex-col bg-[#F9F8F4] text-[#1A1A1A]">
      <MarketingNav path={path} />
      <main className="flex-1">{children}</main>
      <MarketingFooter />
    </div>
  );
}

function MarketingNav({ path }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);

  const linkCls = (href) =>
    `text-[13px] uppercase tracking-[0.22em] font-semibold transition-colors ${
      path === href ? "text-[#1A422D]" : "text-[#4A4A4A] hover:text-[#1A422D]"
    }`;

  return (
    <header className="sticky top-0 z-40 bg-[#F9F8F4]/95 backdrop-blur border-b border-[#E5E3D8]">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-14 h-20 flex items-center justify-between">
        <Link to="/" data-testid="marketing-brand" className="flex items-center gap-3">
          <img src="/logo.png" alt="My Crest Club" className="h-11 w-auto" />
          <span className="hidden sm:block font-serif text-2xl text-[#1A422D] leading-none">
            My Crest Club
          </span>
        </Link>

        {/* Desktop nav — per Build Guide Phase 2: market to Clubs + Vendors
            only. Members are invitation-only and get an "Already Invited?"
            entry point via the Sign In CTA. */}
        <nav className="hidden lg:flex items-center gap-9">
          <Link to="/" data-testid="nav-home" className={linkCls("/")}>Home</Link>
          <Link to="/clubs" data-testid="nav-for-clubs" className={linkCls("/clubs")}>For Clubs</Link>
          <Link to="/vendors" data-testid="nav-for-vendors" className={linkCls("/vendors")}>For Vendors</Link>

          {/* Pricing dropdown — iter57. Public page has two tabs: For Clubs
              and For Vendors. Hover-open on desktop, always-open on mobile. */}
          <div
            className="relative"
            onMouseEnter={() => setPricingOpen(true)}
            onMouseLeave={() => setPricingOpen(false)}
          >
            <button
              type="button"
              data-testid="nav-pricing-trigger"
              className={`flex items-center gap-1.5 text-[13px] uppercase tracking-[0.22em] font-semibold transition-colors ${
                path.startsWith("/pricing") ? "text-[#1A422D]" : "text-[#4A4A4A] hover:text-[#1A422D]"
              }`}
            >
              Pricing <ChevronDown size={14} className={`transition-transform ${pricingOpen ? "rotate-180" : ""}`} />
            </button>
            {pricingOpen && (
              <div
                data-testid="nav-pricing-menu"
                className="absolute left-1/2 -translate-x-1/2 top-full pt-3 min-w-[200px]"
              >
                <div className="bg-white border border-[#E5E3D8] shadow-2xl py-2">
                  <Link
                    to="/pricing/clubs"
                    data-testid="nav-pricing-clubs"
                    className="block px-5 py-2.5 text-[13px] uppercase tracking-[0.22em] font-semibold text-[#4A4A4A] hover:bg-[#F9F8F4] hover:text-[#1A422D]"
                  >
                    For Clubs
                  </Link>
                  <Link
                    to="/pricing/vendors"
                    data-testid="nav-pricing-vendors"
                    className="block px-5 py-2.5 text-[13px] uppercase tracking-[0.22em] font-semibold text-[#4A4A4A] hover:bg-[#F9F8F4] hover:text-[#1A422D]"
                  >
                    For Vendors
                  </Link>
                </div>
              </div>
            )}
          </div>

          <Link to="/about" data-testid="nav-about" className={linkCls("/about")}>About</Link>
          <Link to="/contact" data-testid="nav-support" className={linkCls("/contact")}>Support</Link>
        </nav>

        {/* Right CTA — Sign In (Build Guide uses "Sign In" instead of "Login") */}
        <div className="hidden lg:flex items-center gap-4">
          <Link
            to="/login"
            data-testid="nav-signin"
            className="inline-flex items-center gap-2 px-6 py-2.5 border border-[#1A422D] text-[#1A422D] text-[12px] uppercase tracking-[0.22em] font-semibold hover:bg-[#1A422D] hover:text-[#F9F8F4] transition-colors"
          >
            Sign In
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          type="button"
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-[#1A422D] text-2xl"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? "×" : "≡"}
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-[#E5E3D8] bg-white" data-testid="nav-mobile-drawer">
          <div className="px-6 py-6 space-y-4">
            <MobileLink to="/" onClick={() => setMobileOpen(false)}>Home</MobileLink>
            <MobileLink to="/clubs" onClick={() => setMobileOpen(false)}>For Clubs</MobileLink>
            <MobileLink to="/vendors" onClick={() => setMobileOpen(false)}>For Vendors</MobileLink>
            <div>
              <div className="text-[11px] uppercase tracking-[0.24em] font-semibold text-[#7A7A7A] mb-2">
                Pricing
              </div>
              <div className="pl-3 space-y-3">
                <MobileLink to="/pricing/clubs" onClick={() => setMobileOpen(false)}>For Clubs</MobileLink>
                <MobileLink to="/pricing/vendors" onClick={() => setMobileOpen(false)}>For Vendors</MobileLink>
              </div>
            </div>
            <MobileLink to="/about" onClick={() => setMobileOpen(false)}>About</MobileLink>
            <MobileLink to="/contact" onClick={() => setMobileOpen(false)}>Support</MobileLink>
            <Link
              to="/login"
              onClick={() => setMobileOpen(false)}
              className="mt-4 block text-center px-6 py-3 bg-[#1A422D] text-[#F9F8F4] text-[12px] uppercase tracking-[0.22em] font-semibold"
            >
              Sign In
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

function MobileLink({ to, onClick, children }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="block text-[14px] uppercase tracking-[0.22em] font-semibold text-[#1A1A1A] hover:text-[#1A422D]"
    >
      {children}
    </Link>
  );
}

function MarketingFooter() {
  return (
    <footer className="bg-[#1A422D] text-[#F9F8F4]/90 mt-20">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-14 py-14">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          <div>
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="My Crest Club" className="h-10 w-auto" />
              <span className="font-serif text-xl text-[#F9F8F4]">My Crest Club</span>
            </div>
            <p className="text-sm text-[#F9F8F4]/70 mt-4 leading-relaxed">
              The private club marketplace. Connecting members, golf shops, and premium vendors.
            </p>
          </div>

          <FooterCol title="Solutions">
            <FooterLink to="/clubs" testId="footer-for-clubs">For Clubs</FooterLink>
            <FooterLink to="/vendors" testId="footer-for-vendors">For Vendors</FooterLink>
            <FooterLink to="/pricing">Pricing</FooterLink>
          </FooterCol>

          <FooterCol title="Company">
            <FooterLink to="/about">About</FooterLink>
            <FooterLink to="/pricing">Pricing</FooterLink>
            <FooterLink to="/contact" testId="footer-support">Support</FooterLink>
          </FooterCol>

          <FooterCol title="Legal">
            <FooterLink to="/privacy" testId="footer-privacy">Privacy</FooterLink>
            <FooterLink to="/terms" testId="footer-terms">Terms</FooterLink>
            <FooterLink to="/contact">Contact</FooterLink>
          </FooterCol>
        </div>

        <div className="mt-12 pt-6 border-t border-[#F9F8F4]/15 flex flex-wrap items-center justify-between gap-4 text-[11px] uppercase tracking-[0.24em] text-[#F9F8F4]/60">
          <div>&copy; My Crest Club · Anno 2026</div>
          <Link to="/login" className="hover:text-[#D9C68A]">Sign in →</Link>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.24em] font-semibold text-[#D9C68A]">
        {title}
      </div>
      <ul className="mt-4 space-y-3">{children}</ul>
    </div>
  );
}

function FooterLink({ to, testId, children }) {
  return (
    <li>
      <Link
        to={to}
        data-testid={testId}
        className="text-sm text-[#F9F8F4]/80 hover:text-[#D9C68A] transition-colors"
      >
        {children}
      </Link>
    </li>
  );
}
